import { getTabbableElements } from './helpers';

/**
 * Class representing an element reference with various utility methods
 * for manipulating and interacting with DOM elements.
 */
export default class ElementReference {
  /**
   * The ID of the current event listener.
   * @type {number}
   */
  listenerId: number = 0;

  /**
   * A map of event listener IDs to their corresponding event listener functions.
   * @type {{ [key: number]: EventListenerOrEventListenerObject }}
   */
  eventListeners: { [key: number]: EventListenerOrEventListenerObject } = {};

  /**
   * Creates a new instance of the ElementReference class.
   */
  constructor() {
    this.listenerId = 0;
    this.eventListeners = {};
  }

  /**
   * Sets focus on the specified element.
   * @param {HTMLElement} element - The element to focus.
   */
  focus(element: HTMLElement | null): void {
    if (element) {
      element.focus();
    }
  }

  /**
   * Removes focus from the specified element.
   * @param {HTMLElement} element - The element to blur.
   */
  blur(element: HTMLElement | null): void {
    if (element) {
      element.blur();
    }
  }

  /**
   * Sets focus on the first tabbable element within the specified element.
   * @param {HTMLElement} element - The container element.
   * @param {number} [skip=0] - Number of tabbable elements to skip.
   * @param {number} [min=0] - Minimum number of tabbable elements required to focus.
   */
  focusFirst(element: HTMLElement | null, skip: number = 0, min: number = 0): void {
    if (element) {
      let tabbables = getTabbableElements(element);
      if (tabbables.length <= min) {
        element.focus();
      } else {
        tabbables[skip].focus();
      }
    }
  }

  /**
   * Sets focus on the last tabbable element within the specified element.
   * @param {HTMLElement} element - The container element.
   * @param {number} [skip=0] - Number of tabbable elements to skip from the end.
   * @param {number} [min=0] - Minimum number of tabbable elements required to focus.
   */
  focusLast(element: HTMLElement | null, skip: number = 0, min: number = 0): void {
    if (element) {
      let tabbables = getTabbableElements(element);
      if (tabbables.length <= min) {
        element.focus();
      } else {
        tabbables[tabbables.length - skip - 1].focus();
      }
    }
  }

  /**
   * Saves the current focus state of the specified element.
   * @param {HTMLElement} element - The element to save focus state for.
   */
  saveFocus(element: HTMLElement | null): void {
    if (element) {
      (element as any)['feBlazorLibrary_savedFocus'] = document.activeElement;
    }
  }

  /**
   * Restores the focus state of the specified element.
   * @param {HTMLElement} element - The element to restore focus state for.
   */
  restoreFocus(element: HTMLElement | null): void {
    if (element) {
      let previous = (element as any)['feBlazorLibrary_savedFocus'];
      delete (element as any)['feBlazorLibrary_savedFocus'];
      if (previous) {
        (previous as HTMLElement).focus();
      }
    }
  }

  /**
   * Selects a range of text within the specified element.
   * @param {HTMLInputElement | HTMLTextAreaElement} element - The element to select text in.
   * @param {number} pos1 - The start position of the selection.
   * @param {number} pos2 - The end position of the selection.
   */
  selectRange(element: HTMLInputElement | HTMLTextAreaElement | null, pos1: number, pos2: number): void {
    if (element) {
      if ((element as any).createTextRange) {
        let selRange = (element as any).createTextRange();
        selRange.collapse(true);
        selRange.moveStart('character', pos1);
        selRange.moveEnd('character', pos2);
        selRange.select();
      } else if (element.setSelectionRange) {
        element.setSelectionRange(pos1, pos2);
      } else if (element.selectionStart !== undefined) {
        element.selectionStart = pos1;
        element.selectionEnd = pos2;
      }
      element.focus();
    }
  }

  /**
   * Selects the content of the specified element.
   * @param {HTMLInputElement | HTMLTextAreaElement} element - The element to select.
   */
  select(element: HTMLInputElement | HTMLTextAreaElement | null): void {
    if (element) {
      element.select();
    }
  }

  /**
   * Gets the bounding client rectangle of the specified element.
   * @param {HTMLElement} element - The element to get the bounding rectangle for.
   * @returns {DOMRect} The bounding client rectangle of the element.
   */
  getBoundingClientRect(element: HTMLElement | null): DOMRect | undefined {
    if (!element) 
      return;

    var rect = JSON.parse(JSON.stringify(element.getBoundingClientRect()));

    // Add scroll offsets to the rectangle
    rect.scrollY = window.scrollY || document.documentElement.scrollTop;
    rect.scrollX = window.scrollX || document.documentElement.scrollLeft;

    // Add window dimensions to the rectangle
    rect.windowHeight = window.innerHeight;
    rect.windowWidth = window.innerWidth;
    return rect;
  }

  /**
   * Changes the CSS class of the specified element.
   * @param {HTMLElement} element - The element to change the CSS class for.
   * @param {string} css - The new CSS class.
   */
  changeCss(element: HTMLElement | null, css: string): void {
    if (element) {
      element.className = css;
    }
  }
  
  /**
   * Adds an attribute to the specified HTML element.
   * @param {HTMLElement|string} element - The HTML element or selector to which the attribute will be added.
   * @param {string} attributeName - The name of the attribute to add.
   * @param {string} value - The value of the attribute to add.
   */
  addAttribute(element: HTMLElement | string, attributeName: string, value: string): void {
    if (typeof element === 'string') {
      element = document.querySelector(element) as HTMLElement;
    }
    if (element && attributeName) {
      element.setAttribute(attributeName, value);
    }
  }

  /**
   * Removes an attribute from the specified HTML element.
   * @param {HTMLElement|string} element - The HTML element or selector from which the attribute will be removed.
   * @param {string} attributeName - The name of the attribute to remove.
   */
  removeAttribute(element: HTMLElement | string, attributeName: string): void {
    if (typeof element === 'string') {
      element = document.querySelector(element) as HTMLElement;
    }
    if (element && attributeName) {
      element.removeAttribute(attributeName);
    }
  }

  /**
   * Sets an attribute on the specified HTML element.
   * @param {HTMLElement|string} element - The HTML element or selector on which the attribute will be set.
   * @param {string} attributeName - The name of the attribute to set.
   * @param {string} value - The value of the attribute to set.
   */
  setAttribute(element: HTMLElement | string, attributeName: string, value: string): void {
    if (typeof element === 'string') {
      element = document.querySelector(element) as HTMLElement;
    }
    if (element && attributeName) {
      element.setAttribute(attributeName, value);
    }
  }

  /**
   * Removes an event listener from the specified element.
   * @param {HTMLElement} element - The element to remove the event listener from.
   * @param {string} event - The event type.
   * @param {number} eventId - The ID of the event listener to remove.
   */
  removeEventListener(element: HTMLElement, event: string, eventId: number): void {
    element.removeEventListener(event, this.eventListeners[eventId]);
    delete this.eventListeners[eventId];
  }

  /**
   * Adds an event listener to the specified element that prevents the default action.
   * @param {HTMLElement} element - The element to add the event listener to.
   * @param {string} eventName - The event type.
   * @returns {number} The ID of the added event listener.
   */
  addDefaultPreventingHandler(element: HTMLElement, eventName: string): number {
    let listener = function (e: Event) {
      e.preventDefault();
    }
    element.addEventListener(eventName, listener, { passive: false });
    this.eventListeners[++this.listenerId] = listener;
    return this.listenerId;
  }

  /**
   * Removes an event listener that prevents the default action from the specified element.
   * @param {HTMLElement} element - The element to remove the event listener from.
   * @param {string} eventName - The event type.
   * @param {number} listenerId - The ID of the event listener to remove.
   */
  removeDefaultPreventingHandler(element: HTMLElement, eventName: string, listenerId: number): void {
    this.removeEventListener(element, eventName, listenerId);
  }

  /**
   * Adds multiple event listeners to the specified element that prevent the default action.
   * @param {HTMLElement} element - The element to add the event listeners to.
   * @param {string[]} eventNames - The event types.
   * @returns {number[]} The IDs of the added event listeners.
   */
  addDefaultPreventingHandlers(element: HTMLElement, eventNames: string[]): number[] {
    let listeners: number[] = [];

    for (const eventName of eventNames) {
      let listenerId = this.addDefaultPreventingHandler(element, eventName);
      listeners.push(listenerId);
    }

    return listeners;
  }

  /**
   * Removes multiple event listeners that prevent the default action from the specified element.
   * @param {HTMLElement} element - The element to remove the event listeners from.
   * @param {string[]} eventNames - The event types.
   * @param {number[]} listenerIds - The IDs of the event listeners to remove.
   */
  removeDefaultPreventingHandlers(element: HTMLElement, eventNames: string[], listenerIds: number[]): void {
    for (let index = 0; index < eventNames.length; ++index) {
      const eventName = eventNames[index];
      const listenerId = listenerIds[index];
      this.removeDefaultPreventingHandler(element, eventName, listenerId);
    }
  }

  /**
   * Gets the HTML content of the specified element.
   * @param {HTMLElement} element - The element to get the HTML content from.
   * @returns {string | undefined} The HTML content of the element.
   */
  getHtmlContent(element: HTMLElement | null): string | undefined {
    if (!element) 
      return;

    return element.innerHTML;
  }

};
